/**
 * /sites/:siteId/universal — Universal Components editor.
 *
 * Lists the 4 (or more) UC slots for the site. Each opens a form with the
 * body (with {{var}} placeholders), props_schema (JSON), defaults (JSON).
 * Save shows "affects N articles" if any drafts invoke this UC.
 *
 * Per Ultra Plan v3 §step-18 UI side.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { api } from '../lib/api.ts'

interface UC {
  id: string
  siteId: string
  name: string
  body: string
  propsSchema: unknown
  defaults: unknown
  category: 'text' | 'interactive' | 'faq-set'
  updatedAt: number
}

export function UniversalScreen() {
  const { siteId = 'chc' } = useParams()
  const [list, setList] = useState<UC[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [usage, setUsage] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [edits, setEdits] = useState<Partial<UC>>({})

  useEffect(() => {
    setLoading(true)
    api<{ components: UC[] }>(`/api/sites/${siteId}/universal`)
      .then((res) => {
        setList(res.components)
        if (!selected && res.components.length > 0) setSelected(res.components[0].id)
        return Promise.all(
          res.components.map((c) =>
            api<{ count: number }>(`/api/sites/${siteId}/universal/${c.id}/usage`)
              .then((u) => [c.id, u.count] as const)
              .catch(() => [c.id, 0] as const),
          ),
        )
      })
      .then((entries) => setUsage(Object.fromEntries(entries)))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  const active = useMemo(() => list.find((u) => u.id === selected) ?? null, [list, selected])
  const merged = useMemo(() => ({ ...(active ?? {}), ...edits }) as UC, [active, edits])

  async function save() {
    if (!active) return
    setSaving(true)
    try {
      await api(`/api/sites/${siteId}/universal/${active.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: merged.name,
          body: merged.body,
          props_schema: merged.propsSchema,
          defaults: merged.defaults,
          category: merged.category,
        }),
      })
      setList((l) => l.map((u) => (u.id === active.id ? merged : u)))
      setEdits({})
    } catch (err) {
      alert(`Save failed: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="loading">Loading universal components…</div>
  if (error) return <div className="error-state">{error}</div>

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Universal components</h1>
          <div className="subtitle">
            Per-site shared content. Embed in articles with <code>&lt;UniversalSlot name="…" /&gt;</code>.
          </div>
        </div>
      </div>

      {!loading && list.length === 0 && (
        <div
          style={{
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--radius-lg)',
            padding: '1.5rem 1.75rem',
            background: 'var(--bg-muted)',
            color: 'var(--text-muted)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.4rem' }}>
            No universal components yet
          </div>
          <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Universal components are shared snippets (author bio, disclaimer, pricing footnote, FAQ set) that you edit once and embed in articles via{' '}
            <code>{`<UniversalSlot name="…" />`}</code>. Change them here, and every article that embeds the slot updates on next deploy.
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
            v1.0: components are seeded via D1 migration. UI-side "+ Add component" lands in v1.0.5.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1.5rem' }}>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {list.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => {
                setSelected(u.id)
                setEdits({})
              }}
              className="nav-item"
              style={{
                background: u.id === selected ? 'var(--accent-soft)' : undefined,
                color: u.id === selected ? 'var(--accent)' : undefined,
                fontWeight: u.id === selected ? 600 : 500,
                textAlign: 'left',
                border: 'none',
              }}
            >
              <span>{u.name}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>
                {usage[u.id] ?? 0}
              </span>
            </button>
          ))}
        </nav>

        {active && (
          <div className="editor-form">
            <div className="field">
              <label>Slot id</label>
              <input type="text" value={active.id} readOnly style={{ background: 'var(--bg-muted)' }} />
            </div>
            <div className="field">
              <label>Display name</label>
              <input
                type="text"
                value={merged.name}
                onChange={(e) => setEdits((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Body (use {`{{var}}`} for placeholders)</label>
              <textarea
                value={merged.body}
                onChange={(e) => setEdits((p) => ({ ...p, body: e.target.value }))}
                style={{ minHeight: '8rem' }}
              />
            </div>
            <div className="field">
              <label>Defaults (JSON)</label>
              <textarea
                value={JSON.stringify(merged.defaults, null, 2)}
                onChange={(e) => {
                  try {
                    setEdits((p) => ({ ...p, defaults: JSON.parse(e.target.value) }))
                  } catch {
                    /* don't update until valid JSON */
                  }
                }}
                style={{ minHeight: '5rem' }}
              />
            </div>
            <div className="field">
              <label>Props schema (JSON, optional)</label>
              <textarea
                value={JSON.stringify(merged.propsSchema, null, 2)}
                onChange={(e) => {
                  try {
                    setEdits((p) => ({ ...p, propsSchema: JSON.parse(e.target.value) }))
                  } catch {
                    /* hold */
                  }
                }}
                style={{ minHeight: '5rem' }}
              />
            </div>
            <div className="editor-actions">
              <div style={{ flex: 1, color: 'var(--warn)', fontSize: '0.85rem' }}>
                {(usage[active.id] ?? 0) > 0 && Object.keys(edits).length > 0 ? (
                  <>⚠ Saving affects {usage[active.id]} draft(s).</>
                ) : (
                  <span style={{ color: 'var(--text-faint)' }}>
                    {usage[active.id] ?? 0} draft(s) currently use this slot.
                  </span>
                )}
              </div>
              <button type="button" className="btn" disabled={Object.keys(edits).length === 0} onClick={() => setEdits({})}>
                Revert
              </button>
              <button type="button" className="btn btn-primary" disabled={saving || Object.keys(edits).length === 0} onClick={save}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
